/**
 * ReportsPage.tsx
 *
 * 1:1 port of email_system/pages/reports/campaign-reports.php.
 * Email and SMS analytics with date range filtering, insights, chart placeholder,
 * and top-performing campaigns tables.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  MessageCircle,
  Send,
  Eye,
  MousePointer,
  AlertTriangle,
  BarChart3,
  BellRing,
  ClipboardList,
  CheckCircle2,
  XCircle,
  Lightbulb,
  UserPlus,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/apiClient';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { EmailActivityChart, SmsDeliveryChart, PushActivityChart } from './MarketingReportCharts';
import { DatePicker } from '@/components/ui/DatePicker';

/* ─── Types ──────────────────────────────────────────────────────────────────── */

/**
 * A campaign that produced request-info leads in the range. Its own list —
 * every such campaign, not just the top-10-by-open-rate below — so a campaign
 * that converts well but opens poorly is still visible.
 */
interface LeadCampaign {
  campaignId: number;
  channel: 'email' | 'sms';
  name: string;
  recipientLeads: number;
  referralLeads: number;
  leads: number;
}

/*
 * Every lead field below is optional: an API that predates request-info links
 * sends none of them, and each one reads as zero (or an empty list) when absent.
 */
interface ReportStats {
  emailsSent: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  campaignsSent: number;
  /** Request-info leads created in the range, across channels. */
  leadsGenerated?: number;
  dailyStats: Array<{
    date: string;
    sent: number;
    opens: number;
    clicks: number;
    /** Leads created that day. */
    leads?: number;
  }>;
  sms: {
    campaignsSent: number;
    messagesSent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
    dailyStats: Array<{ date: string; sent: number; delivered: number; failed: number; leads?: number }>;
  };
  push: {
    campaignsSent: number;
    sent: number;
    delivered: number;
    opened: number;
    failed: number;
    noDevice: number;
    openRate: number;
    dailyStats: Array<{ date: string; sent: number; delivered: number; opened: number; failed: number }>;
    campaigns: Array<{
      id: number;
      name: string;
      sentAt: string;
      recipients: number;
      sent: number;
      delivered: number;
      opened: number;
      failed: number;
      noDevice: number;
    }>;
  };
  topCampaigns: Array<{
    id: number;
    name: string;
    sent: number;
    openRate: number;
    clickRate: number;
    leads?: number;
  }>;
  smsCampaigns: Array<{
    id: number;
    name: string;
    sentAt: string;
    recipients: number;
    sent: number;
    delivered: number;
    failed: number;
    leads?: number;
  }>;
  leadCampaigns?: LeadCampaign[];
}

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatRate(n: number): string {
  return `${n.toFixed(1)}%`;
}

/** A lead count off a payload that may predate lead counts. */
function leadCount(n: number | null | undefined): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** Performance badge based on open rate thresholds matching the PHP logic. */
function perfBadge(rate: number) {
  if (rate > 30) return <Badge variant="success">Excellent</Badge>;
  if (rate > 20) return <Badge variant="info">Good</Badge>;
  if (rate > 10) return <Badge variant="warning">Average</Badge>;
  return <Badge variant="error">Poor</Badge>;
}

/** Format a date-time string as "Mon D, YYYY h:mm AM/PM". */
function formatSentAt(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/* ─── Request-info leads (cross-channel) ────────────────────────────────────── */

const TH_CLASS =
  'text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200';
const TD_CLASS = 'py-3 px-3.5 border-b border-slate-100 text-slate-600';

/**
 * Leads from request-info links across email and SMS, above the channel tabs
 * because neither figure belongs to one channel. "Leads" per campaign is the
 * total; recipient and referral split it so a forwarded link cannot pass for
 * conversion among the campaign's own recipients.
 */
function RequestInfoLeadsSummary({ stats }: { stats: ReportStats }) {
  const campaigns = [...(stats.leadCampaigns ?? [])].sort((a, b) => leadCount(b.leads) - leadCount(a.leads));

  return (
    <Card noPadding>
      <div className="p-6">
        <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
          <UserPlus className="h-[18px] w-[18px] text-violet-700" />
          Request-Info Leads
        </h3>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          <StatCard
            label="Leads generated"
            value={leadCount(stats.leadsGenerated)}
            subtitle="From request-info links, email and SMS"
            icon={<UserPlus className="h-5 w-5" />}
            tone="violet"
          />
          <div className="min-w-0">
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Campaigns with leads</h4>
            {campaigns.length === 0 ? (
              <p className="py-4 text-sm text-slate-400">No campaign produced a lead in this date range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className={cn(TH_CLASS, 'rounded-tl-lg')}>Campaign</th>
                      <th className={cn(TH_CLASS, 'text-right')}>Leads</th>
                      <th className={cn(TH_CLASS, 'text-right')}>Recipients</th>
                      <th className={cn(TH_CLASS, 'text-right rounded-tr-lg')}>Referrals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr
                        key={`${c.channel}-${c.campaignId}`}
                        className="hover:bg-slate-50 transition-colors last:[&>td]:border-b-0"
                      >
                        <td className="py-3 px-3.5 border-b border-slate-100">
                          <span className="inline-flex min-w-0 items-center gap-2">
                            <Badge variant={c.channel === 'sms' ? 'success' : 'info'} size="sm">
                              {c.channel === 'sms' ? 'SMS' : 'Email'}
                            </Badge>
                            <span className="font-medium text-slate-800 break-words">{c.name}</span>
                          </span>
                        </td>
                        <td className={cn(TD_CLASS, 'text-right font-semibold text-slate-800 tabular-nums')}>
                          {formatNumber(leadCount(c.leads))}
                        </td>
                        <td className={cn(TD_CLASS, 'text-right tabular-nums')}>
                          {formatNumber(leadCount(c.recipientLeads))}
                        </td>
                        <td className={cn(TD_CLASS, 'text-right tabular-nums')}>
                          {formatNumber(leadCount(c.referralLeads))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────────── */

export default function ReportsPage() {
  const toast = useToast();
  const updateAIContext = usePageContext('Marketing Reports');
  const [tab, setTab] = useState<'email' | 'sms' | 'push'>('email');

  // Date range -- default last 30 days
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Committed dates that actually trigger the query (only updated on "Update Report")
  const [queryFrom, setQueryFrom] = useState(dateFrom);
  const [queryTo, setQueryTo] = useState(dateTo);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['marketing', 'reports', { dateFrom: queryFrom, dateTo: queryTo }],
    queryFn: async () => {
      const res = await apiClient.get('/marketing/reports', {
        params: { dateFrom: queryFrom, dateTo: queryTo },
      });
      return res.data.data as ReportStats;
    },
    staleTime: 60_000,
  });

  const handleUpdateReport = () => {
    setQueryFrom(dateFrom);
    setQueryTo(dateTo);
  };

  const handleExportCSV = () => {
    toast.info('Coming soon', 'CSV export feature coming soon. This will export all report data in CSV format.');
  };

  const handleExportPDF = () => {
    toast.info('Coming soon', 'PDF export feature coming soon. This will generate a formatted PDF report.');
  };

  useEffect(() => {
    updateAIContext({
      filters: `Tab: ${tab}, Date range: ${queryFrom} to ${queryTo}`,
      dataSummary: stats
        ? `Emails sent: ${stats.emailsSent}, Open rate: ${stats.openRate}%, Click rate: ${stats.clickRate}%, SMS campaigns: ${stats.sms.campaignsSent}, SMS delivered: ${stats.sms.delivered}, Request-info leads: ${leadCount(stats.leadsGenerated)}`
        : 'Loading...',
    });
  }, [tab, queryFrom, queryTo, stats, updateAIContext]);

  /* ─── Insights logic (mirrors PHP) ──────────────────────────────────────── */
  const renderInsights = () => {
    if (!stats) return null;
    const lines: string[] = [];

    if (stats.openRate > 25) {
      lines.push(
        `Your open rate of ${stats.openRate.toFixed(1)}% is above industry average (20-25%). Great engagement!`,
      );
    } else if (stats.openRate < 15) {
      lines.push(
        `Your open rate of ${stats.openRate.toFixed(1)}% is below average. Consider improving subject lines.`,
      );
    }

    if (stats.clickRate > 3) {
      lines.push(
        `Click rate of ${stats.clickRate.toFixed(1)}% shows strong content engagement.`,
      );
    }

    if (stats.openRate <= 25 && stats.openRate >= 15 && stats.clickRate <= 3) {
      lines.push('Your email performance is within normal ranges for this period.');
    }

    if (lines.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-blue-50 to-sky-50 border-l-4 border-sky-700 rounded-r-[10px] px-5 py-[18px]">
        <div className="flex items-center gap-2 font-semibold text-[#1e3a5f] text-sm mb-2">
          <Lightbulb className="h-4 w-4" />
          Key Insights
        </div>
        <div className="text-slate-600 text-[13px] leading-[1.7] space-y-0.5">
          {lines.map((line, i) => (
            <p key={i}>&bull; {line}</p>
          ))}
        </div>
      </div>
    );
  };

  /* ─── Render ────────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-6 p-5">
      {/* ── Header with date filter ────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Reports</h2>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label htmlFor="marketing-report-from" className="text-sm text-slate-600">From:</label>
            <DatePicker
              id="marketing-report-from"
              value={dateFrom}
              onChange={setDateFrom}
              wrapperClassName="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="marketing-report-to" className="text-sm text-slate-600">To:</label>
            <DatePicker
              id="marketing-report-to"
              value={dateTo}
              onChange={setDateTo}
              wrapperClassName="w-40"
            />
          </div>
          <Button variant="primary" size="sm" onClick={handleUpdateReport}>
            Update Report
          </Button>
          <Button variant="secondary" size="sm" onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button variant="secondary" size="sm" onClick={handleExportPDF}>
            Export PDF
          </Button>
        </div>
      </div>

      {/* ── Request-info leads, across channels ────────────────────────────── */}
      {!isLoading && stats && <RequestInfoLeadsSummary stats={stats} />}

      {/* ── Channel Tabs ───────────────────────────────────────────────────── */}
      <div className="flex border-b-2 border-slate-200">
        <button
          onClick={() => setTab('email')}
          className={cn(
            'inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 -mb-[2px] transition-colors',
            tab === 'email'
              ? 'text-sky-700 border-sky-700'
              : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50',
          )}
        >
          <Mail className="h-[18px] w-[18px]" />
          Email Analytics
          <Badge variant="info" size="sm">
            Email
          </Badge>
        </button>
        <button
          onClick={() => setTab('sms')}
          className={cn(
            'inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 -mb-[2px] transition-colors',
            tab === 'sms'
              ? 'text-emerald-700 border-emerald-700'
              : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50',
          )}
        >
          <MessageCircle className="h-[18px] w-[18px]" />
          SMS Analytics
          <Badge variant="success" size="sm">
            SMS
          </Badge>
        </button>
        <button
          onClick={() => setTab('push')}
          className={cn(
            'inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 -mb-[2px] transition-colors',
            tab === 'push'
              ? 'text-violet-700 border-violet-700'
              : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50',
          )}
        >
          <BellRing className="h-[18px] w-[18px]" />
          Push Notifications
          <Badge variant="info" size="sm">
            Push
          </Badge>
        </button>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {isLoading || !stats ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          {/* ═══════════════════════════════════════════════════════════════════
              EMAIL ANALYTICS TAB
              ═══════════════════════════════════════════════════════════════════ */}
          {tab === 'email' && (
            <div className="space-y-6">
              {/* Email Stats Grid (5 cards) */}
              <MetricCardGrid columns={5}>
                <StatCard
                  label="Emails Sent"
                  value={stats.emailsSent}
                  icon={<Mail className="h-5 w-5" />}
                  tone="sky"
                />
                <StatCard
                  label="Open Rate"
                  value={formatRate(stats.openRate)}
                  icon={<Eye className="h-5 w-5" />}
                  tone="sky"
                />
                <StatCard
                  label="Click Rate"
                  value={formatRate(stats.clickRate)}
                  icon={<MousePointer className="h-5 w-5" />}
                  tone="sky"
                />
                <StatCard
                  label="Campaigns Sent"
                  value={stats.campaignsSent}
                  icon={<Send className="h-5 w-5" />}
                  tone="violet"
                />
                <StatCard
                  label="Bounce Rate"
                  value={formatRate(stats.bounceRate)}
                  icon={<AlertTriangle className="h-5 w-5" />}
                  tone="red"
                />
              </MetricCardGrid>

              {/* Insights box */}
              {renderInsights()}

              {/* Activity Chart placeholder */}
              <Card noPadding>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                    <BarChart3 className="h-[18px] w-[18px] text-sky-700" />
                    Email Activity Over Time
                  </h3>
                  <EmailActivityChart data={stats.dailyStats} />
                </div>
              </Card>

              {/* Top Performing Campaigns */}
              {stats.topCampaigns.length > 0 && (
                <Card noPadding>
                  <div className="p-6">
                    <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                      <ClipboardList className="h-[18px] w-[18px] text-sky-700" />
                      Top Performing Campaigns
                    </h3>
                    <table className="w-full text-sm border-separate border-spacing-0">
                      <thead>
                        <tr>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200 first:rounded-tl-lg">
                            Campaign
                          </th>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                            Sent
                          </th>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                            Open Rate
                          </th>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                            Click Rate
                          </th>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                            Leads
                          </th>
                          <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200 last:rounded-tr-lg">
                            Performance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topCampaigns.map((c) => (
                          <tr
                            key={c.id}
                            className="hover:bg-slate-50 transition-colors last:[&>td]:border-b-0"
                          >
                            <td className="py-3 px-3.5 border-b border-slate-100">
                              <span className="font-medium text-sky-700 hover:text-sky-600 cursor-pointer">
                                {c.name}
                              </span>
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {formatNumber(c.sent)}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {formatRate(c.openRate)}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {formatRate(c.clickRate)}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600 tabular-nums">
                              {formatNumber(leadCount(c.leads))}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100">
                              {perfBadge(c.openRate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════
              SMS ANALYTICS TAB
              ═══════════════════════════════════════════════════════════════════ */}
          {tab === 'sms' && (
            <div className="space-y-6">
              {/* SMS Stats Grid (5 cards) */}
              <MetricCardGrid columns={5}>
                <StatCard
                  label="Campaigns Sent"
                  value={stats.sms.campaignsSent}
                  icon={<Send className="h-5 w-5" />}
                  tone="emerald"
                />
                <StatCard
                  label="Messages Sent"
                  value={stats.sms.messagesSent}
                  icon={<MessageCircle className="h-5 w-5" />}
                  tone="emerald"
                />
                <StatCard
                  label="Delivered"
                  value={stats.sms.delivered}
                  icon={<CheckCircle2 className="h-5 w-5" />}
                  tone="green"
                />
                <StatCard
                  label="Failed"
                  value={stats.sms.failed}
                  icon={<XCircle className="h-5 w-5" />}
                  tone="red"
                />
                <StatCard
                  label="Delivery Rate"
                  value={`${stats.sms.deliveryRate}%`}
                  icon={<BarChart3 className="h-5 w-5" />}
                  tone="teal"
                />
              </MetricCardGrid>

              {/* Delivery outcome per day */}
              <Card noPadding>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                    <BarChart3 className="h-[18px] w-[18px] text-emerald-700" />
                    SMS Delivery Over Time
                  </h3>
                  <SmsDeliveryChart data={stats.sms.dailyStats ?? []} />
                </div>
              </Card>

              {/* SMS Campaign Breakdown */}
              <Card noPadding>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                    <MessageCircle className="h-[18px] w-[18px] text-emerald-700" />
                    Campaign Breakdown
                  </h3>
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead>
                      <tr>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200 first:rounded-tl-lg">
                          Campaign
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                          Sent At
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                          Recipients
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                          Sent
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                          Delivered
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                          Failed
                        </th>
                        <th className="text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200 last:rounded-tr-lg">
                          Leads
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.smsCampaigns.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center text-slate-400 py-8"
                          >
                            No SMS campaigns in this date range.
                          </td>
                        </tr>
                      ) : (
                        stats.smsCampaigns.map((c) => (
                          <tr
                            key={c.id}
                            className="hover:bg-slate-50 transition-colors last:[&>td]:border-b-0"
                          >
                            <td className="py-3 px-3.5 border-b border-slate-100">
                              <span className="font-medium text-sky-700 hover:text-sky-600 cursor-pointer">
                                {c.name}
                              </span>
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {formatSentAt(c.sentAt)}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {c.recipients}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {c.sent}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {c.delivered}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">
                              {c.failed}
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600 tabular-nums">
                              {formatNumber(leadCount(c.leads))}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════
              PUSH NOTIFICATIONS TAB
              ═══════════════════════════════════════════════════════════════════ */}
          {tab === 'push' && (
            <div className="space-y-6">
              {/* Push Stats Grid (5 cards) */}
              <MetricCardGrid columns={5}>
                <StatCard
                  label="Campaigns Sent"
                  value={stats.push?.campaignsSent ?? 0}
                  icon={<Send className="h-5 w-5" />}
                  tone="violet"
                />
                <StatCard
                  label="Notifications Sent"
                  value={stats.push?.sent ?? 0}
                  icon={<BellRing className="h-5 w-5" />}
                  tone="violet"
                />
                <StatCard
                  label="Opened"
                  value={stats.push?.opened ?? 0}
                  icon={<Eye className="h-5 w-5" />}
                  tone="green"
                />
                <StatCard
                  label="Open Rate"
                  value={formatRate(stats.push?.openRate ?? 0)}
                  icon={<BarChart3 className="h-5 w-5" />}
                  tone="teal"
                />
                <StatCard
                  label="Failed"
                  value={stats.push?.failed ?? 0}
                  icon={<XCircle className="h-5 w-5" />}
                  tone="red"
                />
              </MetricCardGrid>

              {/* No-device note — recipients without a registered app device */}
              {(stats.push?.noDevice ?? 0) > 0 && (
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-l-4 border-violet-700 rounded-r-[10px] px-5 py-[18px]">
                  <div className="flex items-center gap-2 font-semibold text-violet-900 text-sm mb-1">
                    <Lightbulb className="h-4 w-4" />
                    Reach Insight
                  </div>
                  <p className="text-slate-600 text-[13px] leading-[1.7]">
                    {formatNumber(stats.push.noDevice)} recipients had no registered app device, so they never
                    received a push. Encourage patients to install the app to grow your reachable audience.
                  </p>
                </div>
              )}

              {/* Push activity over time */}
              <Card noPadding>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                    <BarChart3 className="h-[18px] w-[18px] text-violet-700" />
                    Push Activity Over Time
                  </h3>
                  <PushActivityChart data={stats.push?.dailyStats ?? []} />
                </div>
              </Card>

              {/* Push Campaign Breakdown */}
              <Card noPadding>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                    <BellRing className="h-[18px] w-[18px] text-violet-700" />
                    Campaign Breakdown
                  </h3>
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead>
                      <tr>
                        {['Campaign', 'Sent At', 'Recipients', 'Sent', 'Delivered', 'Opened', 'Failed', 'No Device'].map((h, i, arr) => (
                          <th
                            key={h}
                            className={cn(
                              'text-left py-2.5 px-3.5 bg-slate-50 font-semibold text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200',
                              i === 0 && 'rounded-tl-lg',
                              i === arr.length - 1 && 'rounded-tr-lg',
                            )}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.push?.campaigns ?? []).length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-slate-400 py-8">
                            No push campaigns in this date range.
                          </td>
                        </tr>
                      ) : (
                        stats.push.campaigns.map((c) => (
                          <tr key={c.id} className="hover:bg-slate-50 transition-colors last:[&>td]:border-b-0">
                            <td className="py-3 px-3.5 border-b border-slate-100">
                              <span className="font-medium text-sky-700 hover:text-sky-600 cursor-pointer">{c.name}</span>
                            </td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatSentAt(c.sentAt)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.recipients)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.sent)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.delivered)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.opened)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.failed)}</td>
                            <td className="py-3 px-3.5 border-b border-slate-100 text-slate-600">{formatNumber(c.noDevice)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
